import { useEffect } from "react";

interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: "website" | "article" | "product";
  price?: number;
  availability?: "instock" | "outofstock";
  schema?: Record<string, any>;
}

function getCanonicalUrl() {
  return new URL(window.location.pathname, window.location.origin).href;
}

export function useSeo(data: SeoData) {
  useEffect(() => {
    const canonicalUrl = getCanonicalUrl();

    // Update title
    document.title = data.title;

    // Update or create meta tags
    const updateMeta = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let meta = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }

      meta.content = content;
    };

    const removeMeta = (property: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      document.querySelector(`meta[${attribute}="${property}"]`)?.remove();
    };

    // Basic meta tags
    updateMeta("description", data.description);
    if (data.keywords) {
      updateMeta("keywords", data.keywords);
    }

    // Open Graph
    updateMeta("og:title", data.title, true);
    updateMeta("og:description", data.description, true);
    updateMeta("og:type", data.type || "website", true);
    updateMeta("og:url", canonicalUrl, true);
    if (data.image) {
      updateMeta("og:image", data.image, true);
    } else {
      removeMeta("og:image", true);
    }

    // Twitter Card
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", data.title);
    updateMeta("twitter:description", data.description);
    if (data.image) {
      updateMeta("twitter:image", data.image);
    } else {
      removeMeta("twitter:image");
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // JSON-LD Schema
    if (data.schema) {
      let script = document.querySelector('script[type="application/ld+json"][data-dynamic]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-dynamic", "true");
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data.schema);
    } else {
      document.querySelector('script[type="application/ld+json"][data-dynamic]')?.remove();
    }

  }, [data]);
}

// Helper to generate product schema
export function generateProductSchema(product: {
  name: string;
  description: string;
  image: string;
  price: number;
  currency?: string;
  brand: string;
  rating?: number;
  reviewCount?: number;
  availability?: "instock" | "outofstock";
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.image,
    "brand": {
      "@type": "Brand",
      "name": product.brand
    },
    "offers": {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": product.currency || "EUR",
      "availability": `https://schema.org/${product.availability === "instock" ? "InStock" : "OutOfStock"}`,
      "url": getCanonicalUrl()
    },
    ...(product.rating && product.reviewCount ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "reviewCount": product.reviewCount,
        "bestRating": 5,
        "worstRating": 1
      }
    } : {})
  };
}

// Helper to generate breadcrumb schema
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": new URL(item.url, window.location.origin).origin + new URL(item.url, window.location.origin).pathname
    }))
  };
}

// Helper to generate HowTo schema for step-by-step guides
export function generateHowToSchema(howTo: {
  name: string;
  description: string;
  image?: string;
  steps: Array<{ name: string; text: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": howTo.name,
    "description": howTo.description,
    ...(howTo.image ? { "image": howTo.image } : {}),
    "step": howTo.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
    })),
    "inLanguage": "de-DE"
  };
}

// Helper to generate comprehensive Article schema
export function generateArticleSchema(article: {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author: {
    name: string;
    role?: string;
  };
  category?: string;
  keywords?: string[];
  wordCount?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.headline,
    "description": article.description,
    "image": {
      "@type": "ImageObject",
      "url": article.image,
      "width": 1200,
      "height": 600
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "author": {
      "@type": "Person",
      "name": article.author.name,
      ...(article.author.role ? { "jobTitle": article.author.role } : {})
    },
    "publisher": {
      "@type": "Organization",
      "name": "zelt.kaufen",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.svg`,
        "width": 600,
        "height": 60
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": getCanonicalUrl()
    },
    ...(article.category ? { "articleSection": article.category } : {}),
    ...(article.keywords && article.keywords.length > 0 ? { "keywords": article.keywords.join(", ") } : {}),
    ...(article.wordCount ? { "wordCount": article.wordCount } : {}),
    "inLanguage": "de-DE"
  };
}

// Helper to generate Organization schema for rich search results
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "zelt.kaufen",
    "url": window.location.origin,
    "logo": {
      "@type": "ImageObject",
      "url": `${window.location.origin}/logo.svg`,
      "width": 600,
      "height": 60
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "mail@pykez.com",
      "contactType": "customer service",
      "availableLanguage": "de"
    }
  };
}

// Helper to generate WebSite schema with search box for Google
export function generateWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "zelt.kaufen",
    "url": window.location.origin,
    "description": "Zeltvergleichportal für Camping, Trekking und Outdoor-Abenteuer",
    "publisher": {
      "@type": "Organization",
      "name": "zelt.kaufen"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${window.location.origin}/zelte?suche={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
}
